import * as React from 'react';
import { FavouriteFiltersAndResult } from 'app/area/favourite/components/favourite-filters-and-result';
import { FavouriteHeader } from 'app/area/favourite/components/favourite-header';
import { FavouriteSearchInput  } from 'app/area/favourite/components/favourite-search';
import { FavouriteError } from 'app/area/favourite/components/favourite-error';
import {
  FavouriteDefinition, SharedState, DefaultSortOrder, FavouriteReplaceDomainNodes,
  UserMessage
} from 'app/types-shared';
import { FavouriteReportDownload } from 'app/area/favourite/types';
import { ElementLoader } from 'ui';
import { StatisticalReportExternalProps } from 'app/area/reports/types';
import { CaseReportExternalProps } from 'app/area/case-report/case-report-container';
import { DomainNode } from 'ui/sort-order/types';

interface FavouriteScreenProps {
  hideHeader: boolean;
  showSearch?: boolean;
  hideFilters: boolean;
  hideFilterReplacements: boolean;
  definition: FavouriteDefinition;
  workingDefinition: FavouriteDefinition;
  searchQueryTemp: string;
  translation: any;
  sharedState: SharedState;
  fetchingSortOptions: boolean;
  fetchingDefinition: boolean;
  fetchingReportCategory: boolean;
  isCreateReportOpen: boolean;
  updateExistingReport: boolean;
  initiallyOpenFilter: boolean;
  report: number;
  favouriteId: number;
  reportCategory?: any;
  onCloseReport: () => void;
  onChange: (value: string) => void;
  onSearch: () => void;
  loadSortOptions: (query?: string) => void;
  updateSortOptions: (sortOrderFields: DefaultSortOrder[]) => void;
  displayExportToZip: () => void;
  onCreateReport: () => void;
  onUpdateReport: () => void;
  downloadReport: (data: FavouriteReportDownload) => void;
  getMultipleSuppliersUrl: () => void;
  urlMultipleSuppliers: string;
  updateDefinition: (transform?: (definition: FavouriteDefinition) => FavouriteDefinition) => void;
  fetchingUpdateReplaceDomainNodes?: boolean;
  fetchingExtendedReplaceDomainNodes?: boolean;
  updateReplaceDomainNodes: (transform: (definition: FavouriteDefinition) => FavouriteDefinition, onSuccess: () => void) => void;
  loadExtendedReplaceDomainNodes: (favouriteId: number, onSuccess: (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => void) => void;
  getSearchRequiredUsers: () => void;
  searchRequiredUsers: boolean;
  sortOptions: DomainNode[];
  caseReportExternalProps?: CaseReportExternalProps;
  statisticalReportExternalProps?: StatisticalReportExternalProps;
  showUpdate: boolean;
  sortOrderFields?: DefaultSortOrder[];
  windowAsScrollableAncestor?: boolean;
  loadDefinitionError?: UserMessage[];
}

const FavouriteScreenHeader = ({
  hideHeader,
  showSearch,
  ...headerProps
}: React.ComponentProps<typeof FavouriteHeader> & Pick<FavouriteScreenProps, 'hideHeader' | 'showSearch'>) => {
  if (!hideHeader) {
    return <FavouriteHeader {...headerProps} />;
  }
  if (!showSearch) {
    return <></>;
  }
  return (
    <div className="row">
      <div className="col-12">
        <FavouriteSearchInput 
          searchQueryTemp={headerProps.searchQueryTemp}
          translation={headerProps.translation}
          onChange={headerProps.onChange}
          onSearch={headerProps.onSearch}
        />
      </div>
    </div>
  );
};

export const FavouriteScreen = ({
  hideHeader,
  showSearch,
  hideFilters,
  hideFilterReplacements,
  definition,
  translation,
  searchQueryTemp,
  sharedState,
  fetchingSortOptions,
  onChange,
  onSearch,
  loadSortOptions,
  updateSortOptions,
  displayExportToZip,
  onCreateReport,
  onUpdateReport,
  downloadReport,
  getMultipleSuppliersUrl,
  urlMultipleSuppliers,
  fetchingDefinition,
  fetchingReportCategory,
  isCreateReportOpen,
  updateExistingReport,
  onCloseReport,
  updateDefinition,
  fetchingUpdateReplaceDomainNodes,
  fetchingExtendedReplaceDomainNodes,
  updateReplaceDomainNodes,
  loadExtendedReplaceDomainNodes,
  initiallyOpenFilter,
  getSearchRequiredUsers,
  report,
  reportCategory,
  workingDefinition,
  sortOptions,
  caseReportExternalProps,
  statisticalReportExternalProps,
  favouriteId,
  showUpdate,
  searchRequiredUsers,
  sortOrderFields,
  windowAsScrollableAncestor,
  loadDefinitionError
}: FavouriteScreenProps) => {
  const fetching = fetchingDefinition || fetchingReportCategory;

  if (loadDefinitionError) {
    return <FavouriteError error={loadDefinitionError} />;
  }
  if (fetching || !definition) {
    return <ElementLoader />;
  }
  return (
    <>
      <FavouriteScreenHeader
        hideHeader={hideHeader}
        showSearch={showSearch}
        definition={definition}
        searchQueryTemp={searchQueryTemp}
        translation={translation}
        sharedState={sharedState}
        fetchingSortOptions={fetchingSortOptions}
        onChange={onChange}
        onSearch={onSearch}
        loadSortOptions={loadSortOptions}
        updateSortOptions={updateSortOptions}
        displayExportToZip={displayExportToZip}
        onCreateReport={onCreateReport}
        onUpdateReport={onUpdateReport}
        downloadReport={downloadReport}
        getMultipleSuppliersUrl={getMultipleSuppliersUrl}
        urlMultipleSuppliers={urlMultipleSuppliers}
        report={report}
        reportCategory={reportCategory}
        sortOptions={sortOptions}
        favouriteId={favouriteId}
        showUpdate={showUpdate}
        sortOrderFields={sortOrderFields}
      />
      <FavouriteFiltersAndResult
        hideFilters={hideFilters}
        hideFilterReplacements={hideFilterReplacements}
        adminPanelAccess={sharedState?.access?.adminPanel}
        isCreateReportOpen={isCreateReportOpen}
        updateExistingReport={updateExistingReport}
        onCloseReport={onCloseReport}
        updateDefinition={updateDefinition}
        updateReplaceDomainNodes={updateReplaceDomainNodes}
        fetchingUpdateReplaceDomainNodes={fetchingUpdateReplaceDomainNodes}
        fetchingExtendedReplaceDomainNodes={fetchingExtendedReplaceDomainNodes}
        loadExtendedReplaceDomainNodes={loadExtendedReplaceDomainNodes}
        initiallyOpenFilter={initiallyOpenFilter}
        definition={definition}
        translation={translation}
        getSearchRequiredUsers={getSearchRequiredUsers}
        report={report}
        workingDefinition={workingDefinition}
        caseReportExternalProps={caseReportExternalProps}
        statisticalReportExternalProps={statisticalReportExternalProps}
        searchRequiredUsers={searchRequiredUsers}
        windowAsScrollableAncestor={windowAsScrollableAncestor}
      />
    </>
  );
};
